import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { ResultsMap } from "@/components/ResultsMap";
import { ProviderFilters } from "@/components/ProviderFilters";
import { TaxonomyPicker, useTaxonomy, toTokens } from "@/components/TaxonomyPicker";
import { MapPin, BadgeCheck, ArrowLeft, Star, X, SlidersHorizontal, Search, Landmark, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { FreeDaysCalendar } from "@/components/FreeDaysCalendar";

const ResultCard = ({ x, from, to }) => (
  <Link to={`/artista/${x.id}`} data-testid={`result-card-${x.id}`} className="card-premium overflow-hidden group flex">
    <img src={mediaUrl(x.media?.photos?.[0]) || "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=400"} alt={x.companyName} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=400"; }} className="w-28 sm:w-36 object-cover shrink-0"/>
    <div className="p-4 min-w-0">
      <div className="font-display text-lg font-bold flex items-center gap-1.5 truncate">{x.companyName}</div>
      <div className="flex flex-wrap gap-1 mt-1">{(x.macros?.length ? x.macros : [x.macroCategory]).slice(0, 2).map((m) => <span key={m} className="chip-navy text-[11px]">{m}</span>)}</div>
      {x.disciplines?.length > 0 && <div className="text-[11px] text-slate-500 mt-1 truncate">{x.disciplines.slice(0, 4).join(" · ")}</div>}
      <div className="text-xs text-slate-600 mt-2 flex items-center gap-1"><MapPin className="w-3 h-3"/>{x.baseLocation}
        {x.distanceKm !== null && x.distanceKm !== undefined && <span className="text-gold font-semibold" data-testid={`result-distance-${x.id}`}>· a {x.distanceKm} km</span>}</div>
      {x.reviewCount > 0 && <div className="text-xs text-gold mt-1 flex items-center gap-1"><Star className="w-3 h-3 fill-gold"/>{x.rating} ({x.reviewCount})</div>}
      {(x.mepaEnabled || x.compliance?.kitComplete) && <div className="flex flex-wrap gap-1 mt-2">
        {x.mepaEnabled && <span data-testid={`result-mepa-${x.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#0B5394] text-white"><Landmark className="w-3 h-3"/>MePA</span>}
        {x.compliance?.kitComplete && <span data-testid={`result-kit-${x.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-600 text-emerald-700"><ShieldCheck className="w-3 h-3"/>Kit PA completo</span>}
      </div>}
      <FreeDaysCalendar x={x} from={from} to={to}/>
    </div>
  </Link>
);

const AREAS = [["all", "Tutti"], ["artist", "Artisti"], ["supplier", "Fornitori"]];
const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const KeywordSearch = ({ t, onPick }) => {
  const [q, setQ] = useState("");
  const words = norm(q).split(/\s+/).filter((w) => w.length >= 3).map((w) => w.slice(0, Math.max(4, w.length - 2)));
  const hits = [];
  if (t && words.length) {
    for (const a of ["artist", "supplier"]) for (const [m, subs] of Object.entries(t[a])) {
      if (words.every((w) => norm(m).includes(w))) hits.push([a, m, null]);
      subs.forEach((s) => { if (s !== "Altro" && words.every((w) => norm(`${s} ${m}`).includes(w))) hits.push([a, m, s]); });
    }
  }
  const pick = (h) => { onPick(...h); setQ(""); };
  return (
    <div className="relative mb-5 max-w-2xl">
      <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"/>
      <input data-testid="keyword-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && hits[0]) pick(hits[0]); }}
        placeholder='Cerca per parola: es. "trampoliere", "service audio", "catering"' className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-full text-sm focus:border-gold outline-none"/>
      {words.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-2xl shadow-xl max-h-72 overflow-y-auto py-1" data-testid="keyword-options">
          {hits.length === 0 && <li className="px-4 py-2 text-sm text-slate-500">Nessuna categoria trovata</li>}
          {hits.slice(0, 12).map(([a, m, s]) => (
            <li key={`${a}${m}${s}`}><button type="button" onClick={() => pick([a, m, s])} data-testid={`keyword-opt-${s || m}`} className="w-full text-left px-4 py-2 text-sm hover:bg-gold/10">
              <span className="font-semibold">{s || m}</span> <span className="text-slate-500">{s ? `· ${m}` : "· tutta la macro-area"} · {a === "artist" ? "Artisti" : "Fornitori"}</span>
            </button></li>
          ))}
        </ul>
      )}
    </div>
  );
};

const PaFilters = ({ value, onChange }) => (
  <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="search-pa-filters">
    <span className="overline text-slate-500 mr-1">Filtri PA</span>
    {[["mepa", "Solo abilitati MePA", Landmark], ["kit", "Kit Trasparenza completo", ShieldCheck]].map(([k, l, Icon]) => (
      <button key={k} type="button" data-testid={`search-filter-${k}`} aria-pressed={!!value[k]} onClick={() => onChange({ ...value, [k]: !value[k] })}
        className={`px-4 py-2 rounded-full text-sm border inline-flex items-center gap-1.5 transition-colors ${value[k] ? "bg-[#0B5394] text-white border-[#0B5394]" : "border-slate-300 hover:border-gold"}`}><Icon className="w-4 h-4"/>{l}</button>
    ))}
  </div>
);

export default function SearchResults() {
  const { user } = useAuth();
  const paViewer = ["public_entity", "business", "admin"].includes(user?.role);
  const [paf, setPaf] = useState({});
  const [params, setParams] = useSearchParams();
  const t = useTaxonomy();
  const initPlace = params.get("lat") ? { name: params.get("comune") || "", prov: params.get("prov") || "", lat: parseFloat(params.get("lat")), lng: parseFloat(params.get("lng")) } : null;
  const [place, setPlace] = useState(initPlace);
  const [extra, setExtra] = useState(() => (params.get("date") ? { date: params.get("date"), ...(params.get("dateTo") ? { dateTo: params.get("dateTo") } : {}) } : {}));
  const [area, setArea] = useState(params.get("area") || "all");
  const [macroOnly, setMacroOnly] = useState((params.get("cat") || "").split("|").filter((c) => c && !c.includes("::")));
  const [picked, setPicked] = useState({});
  const [showCats, setShowCats] = useState(false);
  const [items, setItems] = useState(null);
  const [text, setText] = useState(params.get("q") || "");
  useEffect(() => {
    if ((params.get("q") || "") === text) { return; }
    const n = new URLSearchParams(params);
    if (text) { n.set("q", text); } else { n.delete("q"); }
    setParams(n, { replace: true });
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  const scope = area === "all" ? ["artist", "supplier"] : [area];
  const tokensByArea = useMemo(() => {
    if (!t) return null;
    const out = {};
    scope.forEach((a) => { out[a] = [...macroOnly.filter((m) => t[a][m]), ...toTokens(picked, [a])]; });
    return out;
  }, [t, area, macroOnly, picked]); // eslint-disable-line react-hooks/exhaustive-deps
  const anyTokens = tokensByArea && Object.values(tokensByArea).some((x) => x.length);

  useEffect(() => {
    if (!tokensByArea) return;
    setItems(null);
    const calls = scope.filter((a) => !anyTokens || tokensByArea[a].length).map((a) => api.get(a === "artist" ? "/artists" : "/suppliers", {
      params: { ...extra, ...(text ? { q: text } : {}), ...(paf.mepa ? { mepa: true } : {}), ...(paViewer && paf.kit ? { kit: true } : {}), ...(place ? { lat: place.lat, lng: place.lng } : {}), ...(tokensByArea[a].length ? { cats: tokensByArea[a].join("|") } : {}) } }));
    Promise.all(calls).then((rs) => {
      const seen = new Map();
      rs.flatMap((r) => r.data).forEach((x) => seen.set(x.id, x));
      const merged = [...seen.values()];
      merged.sort((p, q) => (q.premium - p.premium) || ((p.distanceKm ?? 1e9) - (q.distanceKm ?? 1e9)));
      setItems(merged);
    }).catch(() => setItems([]));
  }, [tokensByArea, extra, place, paf, text]); // eslint-disable-line react-hooks/exhaustive-deps

  const chips = [...(text ? [[`“${text}”`, () => setText("")]] : []), ...macroOnly.map((m) => [m, () => setMacroOnly(macroOnly.filter((x) => x !== m))]),
    ...Object.entries(picked).flatMap(([a, sel]) => Object.entries(sel).flatMap(([m, subs]) => subs.map((s) => [`${s}`, () => {
      const n = { ...picked, [a]: { ...sel, [m]: subs.filter((x) => x !== s) } }; if (!n[a][m].length) delete n[a][m]; setPicked(n); }])))];

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="search-results-page">
      <Link to={`/?${params.toString()}#cerca`} className="text-sm text-slate-500 hover:text-navy inline-flex items-center gap-1 mb-4" data-testid="search-back"><ArrowLeft className="w-4 h-4"/>Torna alla Home</Link>
      <span className="overline text-gold block">Cerca artisti e fornitori</span>
      <h1 className="font-display text-4xl sm:text-5xl font-bold mt-2 mb-6">{place ? <>{place.name} {place.prov && <span className="text-slate-400 text-3xl">({place.prov})</span>}</> : "Tutta Italia"}</h1>

      <KeywordSearch t={t} onPick={(a, m, s) => {
        if (!scope.includes(a)) setArea("all");
        if (!s) { if (!macroOnly.includes(m)) setMacroOnly([...macroOnly, m]); return; }
        const sel = { ...(picked[a] || {}) }; sel[m] = [...new Set([...(sel[m] || []), s])]; setPicked({ ...picked, [a]: sel });
      }}/>
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="search-area">
        {AREAS.map(([k, l]) => <button key={k} data-testid={`search-area-${k}`} onClick={() => setArea(k)} className={`px-5 py-2 rounded-full text-sm border transition-colors ${area === k ? "bg-navy text-white border-navy" : "border-slate-300 hover:border-gold"}`}>{l}</button>)}
        <button data-testid="search-cats-toggle" onClick={() => setShowCats(!showCats)} className="btn-pill-outline text-sm ml-auto"><SlidersHorizontal className="w-4 h-4"/>Categorie e sottocategorie</button>
      </div>
      {showCats && <div className="card-premium p-5 mb-4" data-testid="search-cats-panel"><TaxonomyPicker testPrefix="search-tax" areas={scope} value={picked} onChange={(v) => setPicked(v)} withOther={false}/></div>}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="search-cats">
          {chips.map(([l, rm], i) => <button key={l} onClick={rm} data-testid={`search-chip-${i}`} className="chip-gold inline-flex items-center gap-1">{l}<X className="w-3 h-3"/></button>)}
          <button data-testid="search-cats-clear" onClick={() => { setMacroOnly([]); setPicked({}); setText(""); }} className="text-xs text-slate-500 underline">Azzera filtri</button>
        </div>
      )}
      <ProviderFilters initialPlace={initPlace} initialDate={params.get("date")} initialDateTo={params.get("dateTo")} onApply={(p, pl) => { const { lat, lng, ...rest } = p; setExtra(rest); setPlace(pl); }} />
      {paViewer && <PaFilters value={paf} onChange={setPaf} />}

      {items === null && <p className="text-sm text-slate-500">Ricerca in corso…</p>}
      {items && place && <ResultsMap place={place} items={items}/>}
      {items && <div className="text-sm text-slate-500 mb-4" data-testid="search-count">{items.length} risultati{place ? ` che coprono ${place.name}` : ""} · prima i profili Premium{place ? ", poi dal più vicino" : ""}</div>}
      {items && items.length === 0 && <div className="card-premium p-10 text-center text-slate-500" data-testid="search-empty">Nessun profilo corrisponde ai filtri scelti.</div>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{(items || []).map((x) => <ResultCard key={x.id} x={x} from={extra.date} to={extra.dateTo}/>)}</div>
    </div>
  );
}
